import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Home, 
  MapPin, 
  BedDouble, 
  Bath, 
  Square, 
  Copy, 
  Check,
  ChevronLeft,
  ChevronRight,
  User,
  Phone,
  Mail,
  Linkedin,
  Twitter,
  Building2,
  Globe,
  Facebook,
  Instagram,
  Calculator,
  Info,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safe number formatting to prevent React crashes from non-numeric values
const safeNum = (val) => {
  const n = Number(val);
  return isNaN(n) ? 0 : n;
};
const fmtCurrency = (val, decimals = 0) => {
  const n = safeNum(val);
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};
const fmtPct = (val, decimals = 1) => {
  const n = safeNum(val);
  return n.toFixed(decimals);
};

export default function PublicListing() {
  const { shareToken } = useParams();
  const [listing, setListing] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [content, setContent] = useState(null);
  const [agent, setAgent] = useState(null);
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [copiedField, setCopiedField] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    fetchListing();
  }, [shareToken]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/listing/${shareToken}`, {
        timeout: 30000 // 30 second timeout
      });
      setListing(response.data.listing);
      // Limit to first 50 photos max and filter out any with huge base64 data
      const allPhotos = response.data.photos || [];
      const filteredPhotos = allPhotos.slice(0, 50).map(photo => {
        // If any URL is base64 and over 500KB, replace with a placeholder or skip
        const checkUrl = (url) => {
          if (url && url.startsWith('data:') && url.length > 500000) {
            return null; // Too large, skip this URL
          }
          return url;
        };
        return {
          ...photo,
          thumbnail_url: checkUrl(photo.thumbnail_url) || checkUrl(photo.original_photo_url),
          original_photo_url: checkUrl(photo.original_photo_url),
          enhanced_photo_url: checkUrl(photo.enhanced_photo_url),
          staged_photo_url: checkUrl(photo.staged_photo_url)
        };
      }).filter(p => p.thumbnail_url || p.original_photo_url);
      setPhotos(filteredPhotos);
      setContent(response.data.generated_content);
      setAgent(response.data.agent);
      // Support both old single calculation and new multiple calculations
      const calcs = response.data.calculations || [];
      if (calcs.length === 0 && response.data.calculation) {
        calcs.push(response.data.calculation);
      }
      setCalculations(calcs);
    } catch (err) {
      console.error('Public listing fetch error:', err);
      if (err.code === 'ECONNABORTED') {
        setError("Loading took too long. Please try refreshing the page.");
      } else {
        setError(err.response?.data?.detail || "Listing not found or no longer available");
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const getDisplayPhoto = (photo) => {
    // Priority: staged > enhanced > original
    return photo.staged_photo_url || photo.enhanced_photo_url || photo.original_photo_url;
  };

  const getThumbnail = (photo) => {
    // Priority: staged > enhanced > thumbnail > original (match main display priority)
    return photo.staged_photo_url || photo.enhanced_photo_url || photo.thumbnail_url || photo.original_photo_url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Listing Not Found</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/">
            <Button>Go to Homepage</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Logo />
          </Link>
          {agent && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="w-4 h-4" />
              <span>Listed by {agent.name}</span>
            </div>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Photo Gallery */}
        {photos.length > 0 && (
          <div className="relative mb-8 rounded-xl overflow-hidden bg-card">
            <div className="aspect-video relative bg-muted">
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              )}
              <img
                src={getDisplayPhoto(photos[currentPhotoIndex])}
                alt={`Property photo ${currentPhotoIndex + 1}`}
                className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
              />
              
              {photos.length > 1 && (
                <>
                  <button
                    onClick={() => { setImageLoading(true); prevPhoto(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                    data-testid="prev-photo-btn"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => { setImageLoading(true); nextPhoto(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                    data-testid="next-photo-btn"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 text-white text-sm">
                    {currentPhotoIndex + 1} / {photos.length}
                  </div>
                </>
              )}
            </div>
            
            {/* Thumbnail strip */}
            {photos.length > 1 && (
              <div className="flex gap-2 p-4 overflow-x-auto">
                {photos.map((photo, index) => (
                  <button
                    key={photo.id}
                    onClick={() => setCurrentPhotoIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      index === currentPhotoIndex ? 'border-primary' : 'border-transparent'
                    }`}
                  >
                    <img
                      src={getThumbnail(photo)}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Property Details */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge variant={listing.status === "Active" ? "default" : "secondary"}>
                  {listing.status}
                </Badge>
                <span className="text-3xl font-bold text-primary">
                  {formatPrice(listing.price)}
                </span>
              </div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">
                {listing.address}
              </h1>
              <p className="text-muted-foreground flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {listing.city}, {listing.state} {listing.zip_code}
                {listing.neighborhood && ` · ${listing.neighborhood}`}
              </p>
            </div>

            {/* Property Stats */}
            <div className="flex flex-wrap gap-6 py-4 border-y border-border">
              <div className="flex items-center gap-2">
                <BedDouble className="w-5 h-5 text-primary" />
                <span className="font-medium">{listing.bedrooms}</span>
                <span className="text-muted-foreground">Beds</span>
              </div>
              <div className="flex items-center gap-2">
                <Bath className="w-5 h-5 text-primary" />
                <span className="font-medium">{listing.bathrooms}</span>
                <span className="text-muted-foreground">Baths</span>
              </div>
              <div className="flex items-center gap-2">
                <Square className="w-5 h-5 text-primary" />
                <span className="font-medium">{listing.sqft.toLocaleString()}</span>
                <span className="text-muted-foreground">Sq Ft</span>
              </div>
              <div className="flex items-center gap-2">
                <Home className="w-5 h-5 text-primary" />
                <span className="text-muted-foreground capitalize">
                  {listing.property_type?.replace('_', ' ')}
                </span>
              </div>
            </div>

            {/* Key Features & Highlights */}
            {(listing.key_features || listing.highlights) && (
              <div className="space-y-4">
                {listing.key_features && (
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Key Features</h3>
                    <p className="text-muted-foreground">{listing.key_features}</p>
                  </div>
                )}
                {listing.highlights && (
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Highlights</h3>
                    <p className="text-muted-foreground">{listing.highlights}</p>
                  </div>
                )}
              </div>
            )}

            {/* Remarks */}
            {content && content.mls_remarks && (
              <Card className="mt-6">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground">Remarks</h2>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(content.mls_remarks, 'remarks')}
                      className="flex items-center gap-2"
                    >
                      {copiedField === 'remarks' ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                    {content.mls_remarks}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Payment Calculators - Multiple */}
            {calculations.length > 0 && (
              <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Calculator className="text-primary" size={24} />
                    <h2 className="text-xl font-semibold text-foreground">
                      Payment Estimate{calculations.length > 1 ? 's' : ''}
                    </h2>
                  </div>
                  
                  <div className="space-y-6">
                    {calculations.map((calculation, index) => (
                      <div key={calculation.id || index}>
                        {calculation.calculator_type === 'mortgage' && calculation.mortgage_data && (
                          <div className="space-y-4">
                            {calculations.length > 1 && (
                              <h3 className="font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                                <DollarSign size={16} />
                                Mortgage Calculator
                              </h3>
                            )}
                            <div className="text-center bg-primary/10 rounded-lg p-4">
                              <p className="text-sm text-muted-foreground mb-1">Estimated Monthly Payment</p>
                              <p className="text-3xl font-bold text-primary">
                                ${fmtCurrency(calculation.mortgage_data.monthly_payment)}
                              </p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="bg-muted/50 rounded-lg p-3">
                                <p className="text-muted-foreground">Home Price</p>
                                <p className="font-semibold">${fmtCurrency(calculation.mortgage_data.home_price)}</p>
                              </div>
                              <div className="bg-muted/50 rounded-lg p-3">
                                <p className="text-muted-foreground">Down Payment</p>
                                <p className="font-semibold">${fmtCurrency(calculation.mortgage_data.down_payment)} ({safeNum(calculation.mortgage_data.down_payment_percent)}%)</p>
                              </div>
                              <div className="bg-muted/50 rounded-lg p-3">
                                <p className="text-muted-foreground">Interest Rate</p>
                                <p className="font-semibold">{safeNum(calculation.mortgage_data.interest_rate)}%</p>
                              </div>
                              <div className="bg-muted/50 rounded-lg p-3">
                                <p className="text-muted-foreground">Loan Term</p>
                                <p className="font-semibold">{safeNum(calculation.mortgage_data.loan_term)} years</p>
                              </div>
                            </div>
                            
                            <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
                              <p className="font-medium">Monthly Breakdown:</p>
                              <div className="grid grid-cols-2 gap-1">
                                <p>Principal & Interest: ${fmtCurrency(calculation.mortgage_data.monthly_pi) || 'N/A'}</p>
                                <p>Property Tax: ${fmtCurrency(calculation.mortgage_data.monthly_tax)}</p>
                                <p>Insurance: ${fmtCurrency(calculation.mortgage_data.monthly_insurance)}</p>
                                {safeNum(calculation.mortgage_data.monthly_hoa) > 0 && (
                                  <p>HOA: ${fmtCurrency(calculation.mortgage_data.monthly_hoa)}</p>
                                )}
                              </div>
                              <p className="pt-1 font-medium">Loan Amount: ${fmtCurrency(calculation.mortgage_data.loan_amount)}</p>
                            </div>
                          </div>
                        )}
                        
                        {calculation.calculator_type === 'affordability' && calculation.affordability_data && (
                          <div className="space-y-4">
                            {calculations.length > 1 && (
                              <h3 className="font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
                                <Home size={16} />
                                Affordability Calculator
                              </h3>
                            )}
                            <div className="text-center bg-green-100 dark:bg-green-900/30 rounded-lg p-4">
                              <p className="text-sm text-muted-foreground mb-1">You Can Afford Up To</p>
                              <p className="text-3xl font-bold text-green-600">
                                ${fmtCurrency(calculation.affordability_data.max_home_price)}
                              </p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="bg-muted/50 rounded-lg p-3">
                                <p className="text-muted-foreground">Monthly Payment</p>
                                <p className="font-semibold">${fmtCurrency(calculation.affordability_data.monthly_payment)}</p>
                              </div>
                              <div className="bg-muted/50 rounded-lg p-3">
                                <p className="text-muted-foreground">Down Payment</p>
                                <p className="font-semibold">${fmtCurrency(calculation.affordability_data.down_payment)}</p>
                              </div>
                              <div className="bg-muted/50 rounded-lg p-3">
                                <p className="text-muted-foreground">DTI Ratio</p>
                                <p className="font-semibold">{fmtPct(calculation.affordability_data.current_dti)}%</p>
                              </div>
                              <div className="bg-muted/50 rounded-lg p-3">
                                <p className="text-muted-foreground">Housing Ratio</p>
                                <p className="font-semibold">{fmtPct(calculation.affordability_data.current_housing_ratio)}%</p>
                              </div>
                            </div>
                            
                            <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
                              <p className="font-medium">Financial Details:</p>
                              <div className="grid grid-cols-2 gap-1">
                                <p>Annual Income: ${fmtCurrency(calculation.affordability_data.annual_income) || 'N/A'}</p>
                                <p>Monthly Debts: ${fmtCurrency(calculation.affordability_data.monthly_debts)}</p>
                                <p>Interest Rate: {safeNum(calculation.affordability_data.interest_rate) || 'N/A'}%</p>
                                <p>Loan Term: {safeNum(calculation.affordability_data.loan_term) || 30} years</p>
                              </div>
                              <p className="pt-1 font-medium">Loan Amount: ${fmtCurrency(calculation.affordability_data.loan_amount) || 'N/A'}</p>
                            </div>
                          </div>
                        )}
                        
                        {index < calculations.length - 1 && (
                          <div className="border-b border-border/40 my-4" />
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Disclaimer */}
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex gap-2">
                      <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800 dark:text-amber-200">
                        <strong>Disclaimer:</strong> These figures are estimates only and should not be considered as pre-qualification or pre-approval. Actual loan amounts, payments, and terms may vary based on your complete financial profile, credit history, and current market conditions. Please consult with a licensed mortgage professional for accurate figures.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Agent Card - Full Profile */}
            {agent && (
              <Card>
                <CardContent className="p-6">
                  {/* Agent Photo & Brokerage Logo */}
                  <div className="flex flex-col items-center mb-4">
                    {agent.headshot_photo ? (
                      <img 
                        src={agent.headshot_photo} 
                        alt={agent.name}
                        className="w-24 h-24 rounded-full object-cover border-4 border-primary/20 mb-3"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                        <User className="w-12 h-12 text-primary" />
                      </div>
                    )}
                    
                    <h3 className="font-semibold text-lg text-foreground">{agent.name}</h3>
                    {agent.license_number && (
                      <p className="text-xs text-muted-foreground">License #{agent.license_number}</p>
                    )}
                  </div>
                  
                  {/* Brokerage Info - Show logo AND name */}
                  {(agent.brokerage_name || agent.brokerage_logo) && (
                    <div className="flex flex-col items-center gap-1 mb-4 pb-4 border-b border-border">
                      {agent.brokerage_logo && (
                        <img 
                          src={agent.brokerage_logo} 
                          alt={agent.brokerage_name}
                          className="h-10 max-w-[140px] object-contain"
                        />
                      )}
                      {agent.brokerage_name && (
                        <span className="text-sm text-muted-foreground">{agent.brokerage_name}</span>
                      )}
                    </div>
                  )}
                  
                  {/* Bio */}
                  {agent.bio && (
                    <p className="text-sm text-muted-foreground text-center mb-4">
                      {agent.bio}
                    </p>
                  )}
                  
                  {/* Contact Info - More prominent */}
                  <div className="space-y-2 mb-4">
                    {agent.phone && (
                      <a 
                        href={`tel:${agent.phone}`}
                        className="flex items-center justify-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        {agent.phone}
                      </a>
                    )}
                    {agent.email && (
                      <a 
                        href={`mailto:${agent.email}?subject=Inquiry about ${listing.address}`}
                        className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        {agent.email}
                      </a>
                    )}
                  </div>
                  
                  {/* Social Links */}
                  {(agent.social_facebook || agent.social_instagram || agent.social_linkedin || agent.social_twitter) && (
                    <div className="flex justify-center gap-3 mb-4 pt-4 border-t border-border">
                      {agent.social_facebook && (
                        <a 
                          href={agent.social_facebook} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 rounded-full bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20 transition-colors"
                        >
                          <Facebook className="w-4 h-4" />
                        </a>
                      )}
                      {agent.social_instagram && (
                        <a 
                          href={agent.social_instagram} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 rounded-full bg-[#E1306C]/10 text-[#E1306C] hover:bg-[#E1306C]/20 transition-colors"
                        >
                          <Instagram className="w-4 h-4" />
                        </a>
                      )}
                      {agent.social_linkedin && (
                        <a 
                          href={agent.social_linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 rounded-full bg-[#0A66C2]/10 text-[#0A66C2] hover:bg-[#0A66C2]/20 transition-colors"
                        >
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                      {agent.social_twitter && (
                        <a 
                          href={agent.social_twitter} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 rounded-full bg-foreground/10 text-foreground hover:bg-foreground/20 transition-colors"
                        >
                          <Twitter className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  )}
                  
                  {/* Contact Buttons */}
                  <div className="space-y-2">
                    {agent.email && (
                      <a href={`mailto:${agent.email}?subject=Inquiry about ${listing.address}`} className="block">
                        <Button className="w-full gap-2" data-testid="contact-agent-btn">
                          <Mail className="w-4 h-4" />
                          Email Agent
                        </Button>
                      </a>
                    )}
                    {agent.phone && (
                      <a href={`tel:${agent.phone}`} className="block">
                        <Button variant="outline" className="w-full gap-2" data-testid="call-agent-btn">
                          <Phone className="w-4 h-4" />
                          Call {agent.phone}
                        </Button>
                      </a>
                    )}
                    {agent.website && (
                      <a href={agent.website.startsWith('http') ? agent.website : `https://${agent.website}`} target="_blank" rel="noopener noreferrer" className="block">
                        <Button variant="ghost" className="w-full gap-2 text-muted-foreground" data-testid="agent-website-btn">
                          <Globe className="w-4 h-4" />
                          Visit Website
                        </Button>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA Card */}
            <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold text-foreground mb-2">
                  Create Your Own Listings
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Generate professional marketing content with AI in minutes.
                </p>
                <a href={`${API_URL}/signup`}>
                  <Button variant="default" className="w-full" data-testid="signup-cta-btn">
                    Get Started Today
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-16 py-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>Powered by <Link to="/" className="text-primary hover:underline">ListingPro AI</Link></p>
        </div>
      </footer>
    </div>
  );
}
